import type * as SentrySdk from '@sentry/react';

import type { ApiError } from '@/api/errors';
import { getNonEmptyString, getObjectString, pickObjectString } from '@/lib/error-object';

const SERVICE_NAME = 'erp24-frontend';
const MAX_MESSAGE_LEN = 512;
const MAX_STACK_LEN = 4096;

const AUTH_TOKEN_RE = /\b(bearer|basic)\s+[A-Za-z0-9._~+/=-]{8,}/gi;
const TOKEN_LIKE_RE = /(?<![A-Za-z0-9])([A-Za-z0-9._-]{24,})(?![A-Za-z0-9])/g;

export type FrontendErrorEventType =
  | 'fe_runtime_error'
  | 'fe_unhandled_rejection'
  | 'fe_api_error'
  | 'fe_camera_player';

/** Short searchable values a client event adds to its Sentry tags (never URLs). */
export type TelemetryContext = Record<string, string | number | boolean | null>;

export interface FrontendErrorPayload {
  event_type: FrontendErrorEventType;
  timestamp: string;
  service: string;
  env: string;
  message: string;
  stack: string | null;
  route: string;
  page_url: string;
  user_id: string | null;
  request_id: string | null;
  trace_id: string | null;
  span_id: string | null;
  http_status: number | null;
  error_code: string | null;
  deploy_sha: string;
  release_id: string;
  fingerprint: string;
  tags: {
    browser: string;
    component: string;
  };
  /** Sentry level; errors unless a client event says otherwise. */
  level?: 'error' | 'warning';
  /** Event-specific tags (camera id, rendition, …), sanitized. */
  context?: TelemetryContext;
}

interface BuildErrorPayloadArgs {
  eventType: FrontendErrorEventType;
  message: string;
  stack?: string | undefined;
  requestId?: string | undefined;
  traceId?: string | undefined;
  spanId?: string | undefined;
  httpStatus?: number | undefined;
  errorCode?: string | undefined;
  userId?: string | null | undefined;
  route?: string | undefined;
  pageUrl?: string | undefined;
  component?: string | undefined;
}

type TelemetryEmitter = (payload: FrontendErrorPayload, error?: unknown) => void;

interface RouteErrorTelemetrySnapshot {
  message: string;
  stack?: string | undefined;
  requestId?: string | undefined;
  httpStatus?: number | undefined;
  errorCode?: string | undefined;
}

let sinkInitialized = false;
// The Sentry SDK is loaded as its own chunk after start (about 80 KiB off the
// initial bundle). Events raised before it is ready wait in a small queue.
let sentry: typeof SentrySdk | null = null;
let sentryLoading = false;
const SENTRY_QUEUE_LIMIT = 20;
let sentryQueue: SentrySdk.Event[] = [];
let testEmitter: TelemetryEmitter | null = null;

function getPropertyNumber(source: unknown, key: string): number | null {
  if (!source || typeof source !== 'object') {
    return null;
  }
  const value = (source as Record<string, unknown>)[key];
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return null;
  }
  return value;
}

function extractRequestId(data: unknown): string | undefined {
  return pickObjectString(data, ['request_id', 'requestId']) || undefined;
}

function extractErrorCode(data: unknown): string | undefined {
  return pickObjectString(data, ['error', 'code']) || undefined;
}

function extractRouteErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message || 'Unexpected route error';
  }

  if (typeof error === 'string') {
    return error;
  }

  const status = getPropertyNumber(error, 'status');
  const statusText = getObjectString(error, 'statusText');
  const data =
    error && typeof error === 'object' ? (error as Record<string, unknown>).data : undefined;
  const messageFromData =
    getNonEmptyString(data) || pickObjectString(data, ['message', 'detail', 'error', 'title']);

  if (messageFromData) {
    return messageFromData;
  }
  if (statusText) {
    return statusText;
  }
  if (status !== null) {
    return `Route error (${status})`;
  }

  return 'Unexpected route error';
}

function captureRouteErrorSnapshot(error: unknown): RouteErrorTelemetrySnapshot {
  if (error instanceof Error) {
    const maybeStatus = getPropertyNumber(error, 'status');
    const maybeRequestId = extractRequestId(error);
    const maybeErrorCode = extractErrorCode(error);
    return {
      message: error.message || 'Unexpected route error',
      stack: error.stack,
      requestId: maybeRequestId,
      httpStatus: maybeStatus ?? undefined,
      errorCode: maybeErrorCode,
    };
  }

  const status = getPropertyNumber(error, 'status');
  const data =
    error && typeof error === 'object' ? (error as Record<string, unknown>).data : undefined;

  return {
    message: extractRouteErrorMessage(error),
    requestId: extractRequestId(data) || extractRequestId(error),
    httpStatus: status ?? undefined,
    errorCode: extractErrorCode(data) || extractErrorCode(error),
  };
}

function safeTruncate(value: string, maxLen: number): string {
  if (value.length <= maxLen) {
    return value;
  }
  return `${value.slice(0, maxLen)}...<truncated>`;
}

function sanitizeTelemetryText(value: string | null | undefined, maxLen: number): string | null {
  if (!value) {
    return null;
  }
  const masked = value.replace(AUTH_TOKEN_RE, '$1 [REDACTED]').replace(TOKEN_LIKE_RE, '[REDACTED]');
  return safeTruncate(masked, maxLen);
}

function currentRoute(): string {
  return window.location.pathname || '/';
}

function currentPageUrl(): string {
  return window.location.href || '';
}

function browserTag(): string {
  const ua = window.navigator.userAgent || 'unknown';
  if (/chrome/i.test(ua)) return 'chrome';
  if (/safari/i.test(ua) && !/chrome/i.test(ua)) return 'safari';
  if (/firefox/i.test(ua)) return 'firefox';
  if (/edg/i.test(ua)) return 'edge';
  return 'other';
}

function envName(): string {
  return (import.meta.env.VITE_ENVIRONMENT || import.meta.env.MODE || 'unknown').trim();
}

function deploySha(): string {
  const value = import.meta.env.VITE_DEPLOY_SHA?.trim();
  return value || 'unknown';
}

function releaseId(): string {
  const value = import.meta.env.VITE_RELEASE_ID?.trim();
  return value || 'unknown';
}

function buildFingerprint(source: string): string {
  let hash = 0;
  for (let i = 0; i < source.length; i += 1) {
    hash = (hash << 5) - hash + source.charCodeAt(i);
    hash |= 0;
  }
  const normalized = Math.abs(hash).toString(16).padStart(8, '0');
  return `fe-${normalized}`;
}

export function buildFrontendErrorPayload(args: BuildErrorPayloadArgs): FrontendErrorPayload {
  const route = args.route || currentRoute();
  const status = Number.isFinite(args.httpStatus) ? Number(args.httpStatus) : null;
  const code = args.errorCode || null;
  const message = sanitizeTelemetryText(args.message, MAX_MESSAGE_LEN) || 'Unknown frontend error';
  const stack = sanitizeTelemetryText(args.stack, MAX_STACK_LEN);

  const fingerprint = buildFingerprint(
    [args.eventType, code || 'none', route, status === null ? 'none' : String(status)].join('|'),
  );

  return {
    event_type: args.eventType,
    timestamp: new Date().toISOString(),
    service: SERVICE_NAME,
    env: envName(),
    message,
    stack,
    route,
    page_url: args.pageUrl || currentPageUrl(),
    user_id: args.userId ?? null,
    request_id: args.requestId || null,
    trace_id: args.traceId || null,
    span_id: args.spanId || null,
    http_status: status,
    error_code: code,
    deploy_sha: deploySha(),
    release_id: releaseId(),
    fingerprint,
    tags: {
      browser: browserTag(),
      component: args.component || 'app',
    },
  };
}

function emitToSentry(payload: FrontendErrorPayload, error?: unknown): void {
  if (!sentry && !sentryLoading) {
    return;
  }

  const sentryEvent: SentrySdk.Event = {
    level: payload.level ?? 'error',
    message: payload.message,
    tags: {
      ...payload.context,
      event_type: payload.event_type,
      browser: payload.tags.browser,
      component: payload.tags.component,
      deploy_sha: payload.deploy_sha,
      release_id: payload.release_id,
      env: payload.env,
    },
    extra: { frontend_payload: payload } as Record<string, unknown>,
    fingerprint: [payload.fingerprint],
  };

  if (error instanceof Error) {
    sentryEvent.exception = {
      values: [
        {
          type: error.name || 'Error',
          value: error.message || payload.message,
        },
      ],
    };
  }
  if (sentry) {
    sentry.captureEvent(sentryEvent);
  } else if (sentryQueue.length < SENTRY_QUEUE_LIMIT) {
    sentryQueue.push(sentryEvent);
  }
}

function emitFrontendError(payload: FrontendErrorPayload, error?: unknown): void {
  if (testEmitter) {
    testEmitter(payload, error);
    return;
  }
  emitToSentry(payload, error);
}

function onRuntimeError(event: ErrorEvent): void {
  if (event.defaultPrevented) {
    return;
  }
  const payload = buildFrontendErrorPayload({
    eventType: 'fe_runtime_error',
    message: event.message || 'Unhandled runtime error',
    stack: event.error instanceof Error ? event.error.stack : undefined,
    component: 'window.onerror',
  });
  emitFrontendError(payload, event.error);
}

function onUnhandledRejection(event: PromiseRejectionEvent): void {
  if (event.defaultPrevented) {
    return;
  }
  const reason = event.reason;
  let message = 'Unhandled promise rejection';
  let stack: string | undefined;
  if (reason instanceof Error) {
    message = reason.message || message;
    stack = reason.stack;
  } else if (typeof reason === 'string') {
    message = reason;
  }
  const payload = buildFrontendErrorPayload({
    eventType: 'fe_unhandled_rejection',
    message,
    stack,
    component: 'window.onunhandledrejection',
  });
  emitFrontendError(payload, reason);
}

export function initFrontendErrorSink(): void {
  if (sinkInitialized) {
    return;
  }
  sinkInitialized = true;

  const dsn = import.meta.env.VITE_SENTRY_DSN?.trim();
  if (!import.meta.env.PROD || !dsn) {
    return;
  }

  sentryLoading = true;
  void import('@sentry/react')
    .then((sdk) => {
      sdk.init({
        dsn,
        environment: envName(),
        release: releaseId(),
        sendDefaultPii: false,
        tracesSampleRate: 0,
        integrations: (integrations) => {
          // We manually capture `window.onerror` and `window.onunhandledrejection` (with our own
          // redaction + fingerprinting). Disable Sentry's default global handlers to avoid duplicates.
          return integrations.filter((integration) => integration.name !== 'GlobalHandlers');
        },
        beforeSend(event) {
          if (event.request) {
            delete event.request.cookies;
            if (event.request.headers) {
              delete event.request.headers.Authorization;
              delete event.request.headers.authorization;
              delete event.request.headers.Cookie;
              delete event.request.headers.cookie;
            }
          }
          return event;
        },
      });
      sentry = sdk;
      const queued = sentryQueue;
      sentryQueue = [];
      for (const event of queued) sdk.captureEvent(event);
    })
    .catch(() => {
      // Without the SDK chunk, errors still reach the console and the API error rail.
      sentryQueue = [];
    })
    .finally(() => {
      sentryLoading = false;
    });

  window.addEventListener('error', onRuntimeError);
  window.addEventListener('unhandledrejection', onUnhandledRejection);
}

/**
 * The camera live retry protocol (docs/runbooks/store-cameras.md, «Live camera health and
 * reconnects»): 503 answers with Retry-After that the player waits out and shows. The
 * player reports outages itself, rate-limited (`telemetry/cameraPlayer.ts`).
 */
const CAMERA_RETRY_PROTOCOL_CODES = new Set(['camera_offline', 'camera_starting', 'camera_busy']);

export function isExpectedCameraRetryAnswer(error: ApiError): boolean {
  return error.statusCode === 503 && CAMERA_RETRY_PROTOCOL_CODES.has(error.error);
}

export function captureApiErrorTelemetry(
  error: ApiError,
  context?: {
    route?: string;
    pageUrl?: string;
    userId?: string | null;
    component?: string;
  },
): void {
  // Avoid Sentry noise from expected 4xx responses (expired session, validation, conflicts, etc.).
  // We only keep network failures and 5xx here.
  if (!error.isNetworkError() && !error.isServerError()) {
    return;
  }
  if (isExpectedCameraRetryAnswer(error)) {
    return;
  }
  const payload = buildFrontendErrorPayload({
    eventType: 'fe_api_error',
    message: error.message || 'API error',
    stack: error.stack,
    requestId: error.requestId,
    traceId: error.traceId,
    spanId: error.spanId,
    httpStatus: error.statusCode,
    errorCode: error.error,
    route: context?.route,
    pageUrl: context?.pageUrl,
    userId: context?.userId,
    component: context?.component || 'api-client',
  });
  emitFrontendError(payload, error);
}

export function captureRouteErrorTelemetry(
  error: unknown,
  context?: {
    route?: string;
    pageUrl?: string;
    userId?: string | null;
    component?: string;
  },
): void {
  const snapshot = captureRouteErrorSnapshot(error);

  const payload = buildFrontendErrorPayload({
    eventType: 'fe_runtime_error',
    message: snapshot.message,
    stack: snapshot.stack,
    requestId: snapshot.requestId,
    httpStatus: snapshot.httpStatus,
    errorCode: snapshot.errorCode,
    route: context?.route,
    pageUrl: context?.pageUrl,
    userId: context?.userId,
    component: context?.component || 'router.errorElement',
  });

  emitFrontendError(payload, error instanceof Error ? error : undefined);
}

export function captureReactBoundaryErrorTelemetry(
  error: Error,
  context?: {
    route?: string;
    pageUrl?: string;
    userId?: string | null;
    component?: string;
    componentStack?: string;
  },
): void {
  const componentStack = getNonEmptyString(context?.componentStack);
  const stack = [error.stack, componentStack ? `React component stack:\n${componentStack}` : null]
    .filter(Boolean)
    .join('\n\n');

  const payload = buildFrontendErrorPayload({
    eventType: 'fe_runtime_error',
    message: error.message || 'React error boundary caught an exception',
    stack: stack || undefined,
    requestId: extractRequestId(error),
    httpStatus: getPropertyNumber(error, 'status') ?? undefined,
    errorCode: extractErrorCode(error),
    route: context?.route,
    pageUrl: context?.pageUrl,
    userId: context?.userId,
    component: context?.component || 'react.error_boundary',
  });

  emitFrontendError(payload, error);
}

function sanitizeContext(context: TelemetryContext): TelemetryContext {
  const clean: TelemetryContext = {};
  for (const [key, value] of Object.entries(context)) {
    if (value === null || typeof value === 'boolean') clean[key] = value;
    else if (typeof value === 'number') clean[key] = Number.isFinite(value) ? value : null;
    else clean[key] = sanitizeTelemetryText(value, 160);
  }
  return clean;
}

/**
 * A client-observed event that is not an exception (a camera outage seen by a viewer):
 * a Sentry warning grouped by event type, code, route and status. Callers rate-limit.
 */
export function captureClientEventTelemetry(args: {
  eventType: Extract<FrontendErrorEventType, 'fe_camera_player'>;
  message: string;
  errorCode?: string | undefined;
  httpStatus?: number | undefined;
  component: string;
  context: TelemetryContext;
}): void {
  const route = currentRoute();
  const payload: FrontendErrorPayload = {
    ...buildFrontendErrorPayload({
      eventType: args.eventType,
      message: args.message,
      errorCode: args.errorCode,
      httpStatus: args.httpStatus,
      component: args.component,
      route,
      // The page path only: no query string, no media URL.
      pageUrl: route,
    }),
    level: 'warning',
    context: sanitizeContext(args.context),
  };
  emitFrontendError(payload);
}

/** Context for later events of this page (a startup sample before an outage). */
export function addTelemetryBreadcrumb(
  category: string,
  message: string,
  data: TelemetryContext,
): void {
  sentry?.addBreadcrumb({ category, message, level: 'info', data: sanitizeContext(data) });
}

export function __setTelemetryEmitterForTests(emitter: TelemetryEmitter | null): void {
  testEmitter = emitter;
}
